import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BackendAccessService } from '../../Services/APICalls/backend-access-service';

import { LoadingService } from '../../Services/Loading/loading-service.service';
import { SubmissionSuccessDialogComponent } from './submission-success-dialog/submission-success-dialog.component';
import { AuthService } from '../../Services/Auth/auth.service';
import { UserProfileData } from '../../interfaces/user-profile-data';
import { SubmissionsFailureDialogComponent } from './submissions-failure-dialog/submissions-failure-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-support',
  templateUrl: './support.component.html',
  styleUrls: ['./support.component.scss'],

  standalone: false,
})
export class SupportComponent {
  supportForm!: FormGroup;
  userEmail: string = '';
  filePreviews: string[] = [];
  fileNames: string[] = [];
  submissionSuccess: boolean = false;
  userProfile: UserProfileData | null = null;

  products = [
    'Schlage Mobile Access Credentials with ENGAGE\u2122',
    'Schlage Mobile Bluetooth Credential - API/SDK',
    'NFC Mobile Credentials',
    'Subscriptions',
    'Other',
  ];

  constructor(
    private fb: FormBuilder,
    private backend: BackendAccessService,
    private dialog: MatDialog,
    private router: Router,
    private loadingservice: LoadingService,
    private readonly authService: AuthService,
    private readonly snackBar: MatSnackBar
  ) {}
  /* istanbul ignore next */
  ngOnInit(): void {
    this.supportForm = this.fb.group({
      userEmail: ['', [Validators.required, Validators.email]],
      product: ['', Validators.required],
      subject: ['', Validators.required],
      description: ['', Validators.required],
      screenshots: [[]],
    });

    this.authService.userProfile$.subscribe((profile) => {
      if (profile.email) {
        this.supportForm.patchValue({
          userEmail: profile.email,
        });
      }
      this.userProfile = profile;
      console.log('userProfile', this.userProfile);
    });
  }

  /* istanbul ignore next */
  onFileSelected(event: any) {
    const ALLOWED_TYPES = new Set([
      'image/gif',
      'image/jpe',
      'image/jpg',
      'image/jpeg',
      'image/png',
      'image/svg+xml',
      'image/tiff',
      'image/tif',
    ]);

    if (event.target.files.length > 0) {
      const newFiles: File[] = Array.from(event.target.files as FileList);

      const validFiles: File[] = [];
      const invalidFiles: File[] = [];

      newFiles.forEach((file: File) => {
        if (ALLOWED_TYPES.has(file.type)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file);
        }
      });

      if (invalidFiles.length) {
        const invalidNames = invalidFiles.map((f) => f.name).join(', ');
        this.snackBar.open(
          `These files are not supported: ${invalidNames}`,
          'Close',
          {
            duration: 5000,
            panelClass: ['error-snackbar'],
          }
        );
      }
      // Append only valid files to the form control
      const existingFiles: File[] =
        this.supportForm.get('screenshots')?.value || [];
      this.supportForm.patchValue({
        screenshots: [...existingFiles, ...validFiles],
      });

      // Generate previews and file names for valid files
      validFiles.forEach((file: File) => {
        if (file.type.match(/image\/*/) != null) {
          const reader = new FileReader();
          reader.onload = (_event) => {
            this.filePreviews.push(reader.result as string);
          };
          reader.readAsDataURL(file);
        } else {
          this.fileNames.push(file.name);
        }
      });
    }
  }
  /* istanbul ignore next */
  removeScreenshot(index: number): void {
    // Remove the preview and filename based on the index
    this.filePreviews.splice(index, 1);
    this.fileNames.splice(index, 1);

    // Update the form control for screenshots to reflect the removal
    const screenshots: File[] =
      this.supportForm.get('screenshots')?.value || [];
    if (screenshots.length > index) {
      // Check if the index is valid
      screenshots.splice(index, 1); // Remove the file from the array
      this.supportForm.patchValue({ screenshots: screenshots }); // Update the form control
    }
  }
  /* istanbul ignore next */
  submitForm() {
    if (this.supportForm.valid) {
      const combinedDescription = `${this.supportForm.value.product}: ${this.supportForm.value.description}`;

      // Create a new object that excludes the 'product' field
      const { product, ...rest } = this.supportForm.value;
      const formValueToSend = {
        ...rest,
        description: combinedDescription,
      };

      this.loadingservice.show();
      this.backend.submitSupportForm(formValueToSend).subscribe({
        next: (response) => {
          this.loadingservice.hide();
          console.log('Form submitted successfully', response);
          const dialogRef = this.dialog.open(SubmissionSuccessDialogComponent);
          this.handleDialogClosure(dialogRef);
        },
        error: (error) => {
          this.loadingservice.hide();
          console.error('Error submitting form', error);
          const dialogRef = this.dialog.open(SubmissionsFailureDialogComponent);
          this.handleDialogClosure(dialogRef);
        },
      });
    }
  }
  /* istanbul ignore next */
  private handleDialogClosure(dialogRef: MatDialogRef<any>): void {
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const roles = this.userProfile?.['http://allegion.com/claims/roles'];
        if (roles?.includes('Onboarding')) {
          this.router.navigate(['/onboarding-form']);
        } else if (roles?.includes('TechSupport')) {
          this.router.navigate(['/credential-library']);
        } else if (roles?.includes('ZentraAdmin')) {
          this.router.navigate(['/zentra-subscriptions']);
        } else if (roles?.includes('OverturAdmin')) {
          this.router.navigate(['/overtur-admin-subscriptions']);
        } else if (roles?.includes('SATAdmin')) {
          this.router.navigate(['/sat-subscriptions']);
        } else {
          this.router.navigate(['/landing']);
        }
      }
    });
  }
}
